import React, { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Typeahead } from "react-bootstrap-typeahead";
import { FormattedMessage, useIntl } from "react-intl";

import "./SearchSelect.scss";
import { ReactComponent as CaretUp } from "assets/images/icons/CaretUp.svg";
import { filterWhenNonSelected } from "helpers/commonHelpers";

interface ISearchSelectProps {
  name: string;
  options: any;
  label?: React.ReactNode;
  required?: boolean | string;
  validations?: object;
  classes?: string;
  placeholder?: string;
  defaultValue?: any;
  filterBy?: string[] | Function;
  extraInfo?: React.ReactNode;
  onSelect?: Function;
  onInputChange?: (value: string) => void;
  handleRenderOption?: Function;
  onFocus?: Function;
  disable?: boolean;
  handleOnBlur?: Function;
  // if this option will be true it will work like select + input.
  enableManualValue?: boolean;
  capitalizeOptions?: boolean;
  preselect?: boolean;
  shouldUnregister?: boolean;
  isLoading?: boolean;
  disableAutoClear?: boolean;
  allowNew?: boolean;
  manualName?: string;
  newSelectionPrefix?: string;
  testId?: string;
}

function SearchSelect({
  name,
  options,
  label,
  required,
  validations,
  classes,
  placeholder,
  defaultValue,
  filterBy = filterWhenNonSelected,
  extraInfo,
  onSelect,
  onInputChange,
  handleRenderOption,
  onFocus,
  disable,
  enableManualValue,
  capitalizeOptions,
  handleOnBlur,
  preselect = true,
  disableAutoClear = false,
  shouldUnregister,
  isLoading,
  allowNew,
  manualName,
  newSelectionPrefix,
  testId,
}: ISearchSelectProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { setValue, control, getValues } = useFormContext();
  const typeheadRef = useRef<any>(null);

  const getSelectedValue = (value: any) => {
    if (value?.toString()) {
      // eslint-disable-next-line eqeqeq
      const selectedOption = options?.filter(
        (item: any) => item.value?.toString()?.toLowerCase() === value?.toString()?.toLowerCase(),
      );
      if (!selectedOption?.length) {
        return [{ label: value, value }];
      }
      return [selectedOption[0]];
    }
    return [];
  };
  useEffect(() => {
    if (!preselect || !options || options.length === 0 || options.length > 1) return;
    if (
      !options[0].value ||
      options[0].value === getValues(name) ||
      (typeof options[0].value === "string" && options[0].value.startsWith(getValues(name)))
    )
      return;
    setValue(name, options[0].value);
  }, [options]);

  const handleSelectBlur = () => {
    if (handleOnBlur) {
      handleOnBlur();
    }
    if (
      !enableManualValue &&
      isOpen &&
      typeheadRef?.current?.clear &&
      !disableAutoClear &&
      !options?.find((item: any) => item.value?.toString()?.toLowerCase() === getValues(name)?.toString()?.toLowerCase())
    ) {
      typeheadRef.current.clear();
    }
    setIsOpen(false);
  };

  return (
    <div
      className={`search-select ${classes}`}
      id={name}
      data-testid={testId || `search-select-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`}
    >
      <Controller
        control={control}
        rules={{ required, ...validations }}
        name={name}
        shouldUnregister={shouldUnregister}
        {...(defaultValue && { defaultValue })}
        render={({ field: { onChange, onBlur, value, ref: controllerRef, ...rest }, fieldState: { error } }) => (
          <>
            {label && (
              <label htmlFor={name} className="d-flex mb-xxs">
                {label}
                {required && <span className="text-danger ml-1">*</span>}
              </label>
            )}
            <Typeahead
              allowNew={allowNew}
              newSelectionPrefix={newSelectionPrefix}
              className={capitalizeOptions ? "text-capitalize" : ""}
              id={name}
              options={options}
              placeholder={placeholder}
              paginationText={intl.formatMessage({
                id: "components.searchSelect.paginationText",
                defaultMessage: "More...",
              })}
              selected={getSelectedValue(value)}
              onMenuToggle={setIsOpen}
              onFocus={() => {
                if (onFocus) {
                  onFocus();
                }
                setIsOpen(true);
              }}
              onBlur={handleSelectBlur}
              open={isOpen}
              {...(filterBy && { filterBy })}
              {...(defaultValue && { defaultSelected: options.filter((item: any) => item.value === defaultValue) })}
              onChange={(event: any) => {
                const { label, value } = event?.[0] || {};
                if (enableManualValue && allowNew && label && !value) {
                  if (label) {
                    setValue(manualName ?? name, label || "", { shouldValidate: true });
                    setIsOpen(false);
                    return;
                  }
                }
                if (manualName) setValue(manualName, "");
                if (enableManualValue && !event?.[0]?.value) {
                  return;
                }
                if (onSelect) {
                  onSelect(event);
                }
                setValue(name, event?.[0]?.value || "", { shouldValidate: true });
                if (event?.[0]?.value) {
                  setIsOpen(false);
                }
              }}
              disabled={disable}
              onInputChange={(value) => {
                if (enableManualValue) {
                  setValue(name, value || "", { shouldValidate: true });
                }
                if (onInputChange) {
                  onInputChange(value);
                }
              }}
              inputProps={{
                "data-testid": testId
                  ? `${testId}-input`
                  : `search-select-input-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`,
                style: {
                  height: 26,
                  borderRadius: 10,
                  padding: 11,
                  paddingRight: 20,
                  fontSize: 11,
                  zIndex: isOpen ? 1115 : 0,
                },
                className: error ? "invalid-input bg-white" : ` ${isOpen ? "bg-white search-box search-select-input" : ""}`,
              }}
              {...(handleRenderOption && {
                renderMenuItemChildren: (option, { text }) => handleRenderOption({ option, text }),
              })}
              ref={(innerRef) => {
                typeheadRef.current = innerRef;
                controllerRef(innerRef);
              }}
              {...rest}
            >
              <span
                style={{
                  position: "absolute",
                  top: isLoading ? 6 : 9,
                  right: isLoading ? 12 : 18,
                  zIndex: isOpen ? 99999 : 0,
                }}
              >
                {isLoading ? (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ color: isOpen ? "white" : "black" }}
                  />
                ) : (
                  <CaretUp
                    className={`position-absolute align-self-center cursor-pointer ${isOpen ? "invert-color" : ""}`}
                    onClick={() => {
                      if (!disable) setIsOpen(!isOpen);
                    }}
                    style={{
                      transition: "all .3s",
                      marginTop: "2px",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                )}
              </span>
            </Typeahead>
            {error && (
              <div className="d-block font-b3 invalid-feedback">
                {error?.message ? (
                  error.message
                ) : (
                  <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
                )}
              </div>
            )}
            {extraInfo && extraInfo}
          </>
        )}
      />
    </div>
  );
}

export default SearchSelect;

SearchSelect.defaultProps = {
  label: "",
  placeholder: "",
  classes: "",
  required: false,
  filterBy: undefined,
  extraInfo: undefined,
  validations: undefined,
  defaultValue: undefined,
  onInputChange: undefined,
  onSelect: undefined,
  handleRenderOption: undefined,
  disable: false,
  onFocus: undefined,
  enableManualValue: false,
  capitalizeOptions: false,
  handleOnBlur: undefined,
  preselect: true,
  shouldUnregister: false,
  isLoading: false,
  disableAutoClear: false,
  allowNew: false,
  manualName: undefined,
  newSelectionPrefix: undefined,
  testId: undefined,
};
